import type { FieldWrapperInjectedProps } from "@birdwatch/ui/form";
import { Time } from "@internationalized/date";
import { useToggle } from "@uidotdev/usehooks";
import { cn } from "cn";
import { addDays } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { useComboboxOpen } from "@/components/ui/combobox";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { BwCalendarToDoLine, BwChevronDownLine } from "@/components/ui/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Typography } from "@/components/ui/typography";
import useTimezone from "@/hooks/use-timezone";
import {
  combineDateAndTime,
  DEFAULT_TIME_ZONE,
  formatDate,
  ISO_DATE_FORMAT,
} from "@/libs/date-fns";
import { DueDateCalendar } from "./due-date-calendar";

/** `key` is the preset key (or `"CUSTOM"`); `value` is the ISO instant. Both `""` when unset. */
export type DueDateValue = {
  key: string;
  value: string;
};

type DueDateSelectProps = FieldWrapperInjectedProps & {
  className?: string;
  value: DueDateValue;
  onChange: (value: DueDateValue) => void;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  disabled?: boolean;
  "data-test"?: string;
};

const DUE_DATE = {
  URGENT: "URGENT",
  WITHIN_A_WEEK: "WITHIN_A_WEEK",
  WITHIN_A_MONTH: "WITHIN_A_MONTH",
  CUSTOM: "CUSTOM",
};

const DUE_DATE_LABEL = {
  [DUE_DATE.URGENT]: "Urgent 🚨",
  [DUE_DATE.WITHIN_A_WEEK]: "Within a week",
  [DUE_DATE.WITHIN_A_MONTH]: "Within a month",
  [DUE_DATE.CUSTOM]: "Pick a date",
};

// Notes
// Step 1: Whatever timezone you are. addDays(new Date(), 1).toISOString(),
// Step 2: Convert string to timezone you want (Due to the object datetime still be at local timezone -> have to use the string)
// Step 3: Implement the logic with correct time of specific timezone. Eg: set 00:00
const convertTimeToDefaultTimeZone = (plusDays: number, timezone?: string) => {
  const newDay = addDays(new Date(), plusDays).toISOString();
  const convertedNewDayToTimeZone = formatDate(
    toZonedTime(newDay, timezone || DEFAULT_TIME_ZONE).toISOString(),
    ISO_DATE_FORMAT,
  );
  return combineDateAndTime(
    convertedNewDayToTimeZone,
    new Time(0, 0),
    timezone || DEFAULT_TIME_ZONE,
  );
};

export default function DueDateSelect({
  className,
  value,
  onChange,
  open,
  defaultOpen,
  onOpenChange,
  modal = true,
  disabled,
  ...props
}: DueDateSelectProps) {
  const [isOpen, setOpen] = useComboboxOpen({
    open,
    defaultOpen,
    onOpenChange,
    disabled,
  });
  const [openCalendar, toggleCalendar] = useToggle(false);
  const { timeZone } = useTimezone();

  const dueDateOptions = [
    {
      key: DUE_DATE.URGENT,
      label: DUE_DATE_LABEL[DUE_DATE.URGENT],
      value: convertTimeToDefaultTimeZone(1),
    },
    {
      key: DUE_DATE.WITHIN_A_WEEK,
      label: DUE_DATE_LABEL[DUE_DATE.WITHIN_A_WEEK],
      value: convertTimeToDefaultTimeZone(7),
    },
    {
      key: DUE_DATE.WITHIN_A_MONTH,
      label: DUE_DATE_LABEL[DUE_DATE.WITHIN_A_MONTH],
      value: convertTimeToDefaultTimeZone(30),
    },
    {
      key: DUE_DATE.CUSTOM,
      label: DUE_DATE_LABEL[DUE_DATE.CUSTOM],
      value: "",
    },
  ];

  const formatTypeValue = (value: string) => {
    return formatDate(value, "MMM d", timeZone);
  };

  return (
    <>
      <Popover modal={modal} open={isOpen} onOpenChange={setOpen}>
        <PopoverTrigger disabled={disabled} className="group" asChild>
          <div
            data-test="due-date-select-trigger"
            role="combobox"
            className={cn(
              /* sorted */
              "flex",
              "items-center justify-between",
              "w-full min-h-10",
              "px-3 py-2",
              "outline outline-outline-dark-low rounded-lg cursor-pointer",
              "aria-invalid:outline-outline-negative-max data-[state=open]:outline-outline-dark-max data-[state=open]:outline-[1.5px] group-disabled:cursor-not-allowed group-disabled:bg-bg-dark-low group-disabled:opacity-50",
              value?.key !== "" && value?.value !== "" && "text-text-dark-min",
              className,
            )}
            {...props}
          >
            {value?.key !== "" && value?.value !== "" ? (
              <div className="flex items-center gap-2">
                <BwCalendarToDoLine
                  size={20}
                  className="text-text-dark shrink-0"
                />
                <Typography
                  className="flex items-center gap-2 text-text-dark"
                  size="sm"
                >
                  {formatTypeValue(value?.value)}
                </Typography>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <BwCalendarToDoLine
                  size={20}
                  className="text-text-dark-low shrink-0"
                />
                <Typography className="text-text-dark-min" size="sm">
                  Select a day
                </Typography>
              </div>
            )}

            <BwChevronDownLine
              size={16}
              className="text-text-dark-high shrink-0"
            />
          </div>
        </PopoverTrigger>
        <PopoverContent className="p-0">
          <Command>
            <CommandList>
              <CommandGroup>
                {dueDateOptions.map((option: any) => (
                  <CommandItem
                    value={option.key}
                    key={option.key}
                    onSelect={() => {
                      setOpen(false);
                      // Custom date
                      if (option.key === DUE_DATE.CUSTOM) {
                        toggleCalendar(true);
                      } else {
                        onChange({
                          key: option.key,
                          value: option.value,
                        });
                      }
                    }}
                  >
                    <div className="flex items-center justify-between w-full">
                      <Typography
                        className="flex items-center gap-2 text-text-dark"
                        size="sm"
                      >
                        {option.label}
                      </Typography>

                      <Typography className="text-sm leading-5 text-text-dark-low">
                        {formatTypeValue(option.value)}
                      </Typography>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <DueDateCalendar
        open={openCalendar}
        onOpenChange={toggleCalendar}
        dueDate={value}
        onChange={onChange}
        calendarDisabled={{
          before: addDays(new Date(), 2),
        }}
      />
    </>
  );
}
